import Input from '../ui/Input'
import Button from '../ui/Button'
import { useLang } from '../../contexts/LangContext'
import { LANGUAGE_OPTIONS, type OnboardingProfileBasics } from '../../lib/providerOnboarding'

interface ProfileBasicsStepProps {
  profileBasics: OnboardingProfileBasics
  saving: boolean
  onChange: (field: keyof OnboardingProfileBasics, value: string) => void
  onToggleLanguage: (code: string) => void
  onNext: () => void
}

export default function ProfileBasicsStep({
  profileBasics,
  saving,
  onChange,
  onToggleLanguage,
  onNext,
}: ProfileBasicsStepProps) {
  const { locale, t } = useLang()
  const bioLength = profileBasics.bio.trim().length

  return (
    <div className="space-y-8">
      <div className="grid gap-8 lg:grid-cols-[240px_minmax(0,1fr)]">
        <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
            {t('Φωτογραφία προφίλ', 'Profile photo')}
          </p>
          <div className="mt-5 flex justify-center">
            {profileBasics.avatarUrl.trim() ? (
              <img
                src={profileBasics.avatarUrl}
                alt={t('Προεπισκόπηση avatar', 'Avatar preview')}
                className="h-36 w-36 rounded-[2rem] object-cover shadow-sm"
              />
            ) : (
              <div className="flex h-36 w-36 items-center justify-center rounded-[2rem] bg-slate-200 text-4xl font-black text-slate-500">
                ?
              </div>
            )}
          </div>
          <p className="mt-4 text-sm leading-6 text-slate-500">
            {t(
              'Χρησιμοποίησε ένα καθαρό πορτρέτο ή το λογότυπο της δουλειάς σου.',
              'Use a clear portrait or your business logo.',
            )}
          </p>
        </div>

        <div className="space-y-6">
          <div>
            <label htmlFor="provider-bio" className="text-sm font-medium text-slate-700">
              {t('Bio', 'Bio')}
              <span className="ml-0.5 text-red-500">*</span>
            </label>
            <textarea
              id="provider-bio"
              rows={6}
              value={profileBasics.bio}
              onChange={(event) => onChange('bio', event.target.value)}
              placeholder={t(
                'Πες λίγα λόγια για την εμπειρία σου, τα δυνατά σου σημεία και τι μπορούν να περιμένουν οι πελάτες.',
                'Tell customers about your experience, strengths, and what they can expect from you.',
              )}
              className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <div className="mt-2 flex items-center justify-between gap-3 text-xs">
              <p className="text-slate-500">
                {t('Στόχευσε σε τουλάχιστον 50 χαρακτήρες.', 'Aim for at least 50 characters.')}
              </p>
              <span className={bioLength >= 50 ? 'font-semibold text-emerald-600' : 'text-slate-400'}>
                {bioLength} / 50
              </span>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <Input
              type="number"
              min="0"
              step="1"
              label={t('Χρόνια εμπειρίας', 'Years of experience')}
              placeholder="5"
              value={profileBasics.yearsExperience}
              onChange={(event) => onChange('yearsExperience', event.target.value)}
              required
            />
            <Input
              type="text"
              inputMode="decimal"
              label={t('Ωριαία χρέωση (EUR)', 'Hourly rate (EUR)')}
              placeholder="15"
              value={profileBasics.hourlyRateEur}
              onChange={(event) => onChange('hourlyRateEur', event.target.value)}
              required
            />
          </div>

          <Input
            type="url"
            label={t('URL φωτογραφίας', 'Avatar URL')}
            placeholder="https://"
            value={profileBasics.avatarUrl}
            onChange={(event) => onChange('avatarUrl', event.target.value)}
            hint={t(
              'Προς το παρόν το onboarding δέχεται σύνδεσμο εικόνας αντί για upload.',
              'For now onboarding accepts an image URL instead of a file upload.',
            )}
            required
          />

          <div>
            <p className="text-sm font-medium text-slate-700">
              {t('Γλώσσες που μιλάς', 'Languages you speak')}
              <span className="ml-0.5 text-red-500">*</span>
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {LANGUAGE_OPTIONS.map((language) => {
                const selected = profileBasics.languages.includes(language.code)

                return (
                  <button
                    key={language.code}
                    type="button"
                    onClick={() => onToggleLanguage(language.code)}
                    className={[
                      'rounded-full border px-4 py-2 text-sm font-semibold transition-colors',
                      selected
                        ? 'border-primary-600 bg-primary-600 text-white'
                        : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50',
                    ].join(' ')}
                  >
                    {locale === 'el' ? language.labelEl : language.labelEn}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button type="button" size="lg" loading={saving} onClick={onNext}>
          {t('Αποθήκευση και συνέχεια', 'Save and continue')}
        </Button>
      </div>
    </div>
  )
}
